// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode } from 'react';

import type { GeometriCommand, GeometriTool } from '../../../bygning/geometriKeys';

/**
 * Rediger › Geometri's CAD icons (the design mock's set, frontend-u0o
 * design.md §3): 24×24 strokes in `currentColor`, drawn at 18–20 px.
 */
const PATHS = {
  select: (
    <>
      <path d="M5 3l12 7-5.2 1.3L9.5 17z" /><path d="M12 12l4.5 5" />
    </>
  ),
  wall: (
    <>
      <path d="M3 8h18M3 15h18" /><path d="M6 8l-3 3M11 8l-7 7M16 8l-7 7M21 8l-7 7M21 13l-2 2" />
    </>
  ),
  floor: (
    <>
      <path d="M3 15l5-6h13l-5 6z" /><path d="M3 15v2h13l5-6v-2" />
    </>
  ),
  room: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="1" /><path d="M9 12h6" /><path d="M10 15h4" />
    </>
  ),
  zone: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="1" strokeDasharray="3 2.5" /><rect x="8" y="8" width="8" height="8" rx="1" />
    </>
  ),
  door: (
    <>
      <path d="M4 20h16" /><path d="M6 20V5" /><path d="M6 5a15 15 0 0 1 13 15" strokeDasharray="2 2" />
    </>
  ),
  window: (
    <>
      <rect x="3" y="8" width="18" height="8" rx="1" /><path d="M3 12h18" />
    </>
  ),
  opening: (
    <>
      <path d="M3 9h6v6H3M21 9h-6v6h6" /><path d="M11 12h2" />
    </>
  ),
  stair: (
    <>
      <path d="M3 20h4v-4h4v-4h4V8h4V4" /><path d="M3 20h18" />
    </>
  ),
  measure: (
    <>
      <path d="M3 16l13-13 5 5-13 13z" /><path d="M7 12l2 2M10 9l2 2M13 6l2 2" />
    </>
  ),
  dimension: (
    <>
      <path d="M4 7v10M20 7v10M4 12h16" /><path d="M7 9.5L4 12l3 2.5M17 9.5l3 2.5-3 2.5" />
    </>
  ),
  move: (
    <>
      <path d="M12 3v18M3 12h18" /><path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" />
    </>
  ),
  copy: (
    <>
      <rect x="8" y="8" width="12" height="12" rx="1.5" /><path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
    </>
  ),
  rotate: (
    <>
      <path d="M20 12a8 8 0 1 1-2.3-5.7" /><path d="M20 4v4h-4" />
    </>
  ),
  mirror: (
    <>
      <path d="M12 3v18" strokeDasharray="2 2" /><path d="M9 7L4 17h5zM15 7l5 10h-5z" />
    </>
  ),
  offset: (
    <>
      <path d="M4 18L18 4" /><path d="M9 20L20 9" strokeDasharray="2.5 2" /><path d="M12 11l3 3" />
    </>
  ),
  split: (
    <>
      <circle cx="6" cy="7" r="2.5" /><circle cx="6" cy="17" r="2.5" /><path d="M8 8.5L20 17M8 15.5L20 7" />
    </>
  ),
  merge: (
    <>
      <path d="M3 12h6M15 12h6" /><path d="M9 8.5l3 3.5-3 3.5M15 8.5l-3 3.5 3 3.5" />
    </>
  ),
  align: (
    <>
      <path d="M5 3v18" strokeDasharray="2 2" /><path d="M8 6h11v4H8zM8 14h7v4H8z" />
    </>
  ),
  join: (
    <>
      <path d="M4 18V8a3 3 0 0 1 3-3h13" /><circle cx="4" cy="5" r="1.4" />
    </>
  ),
  trim: (
    <>
      <path d="M4 6h16" /><path d="M12 6v14" /><path d="M9 17l3 3 3-3" />
    </>
  ),
  delete: (
    <>
      <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
    </>
  ),
  revert: (
    <>
      <path d="M9 14L4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" />
    </>
  ),
  undo: (
    <>
      <path d="M9 14L4 9l5-5" /><path d="M4 9h11a5 5 0 0 1 0 10h-3" />
    </>
  ),
  redo: (
    <>
      <path d="M15 14l5-5-5-5" /><path d="M20 9H9a5 5 0 0 0 0 10h3" />
    </>
  ),
  layers: (
    <>
      <path d="M12 4l9 5-9 5-9-5z" /><path d="M3 14l9 5 9-5" />
    </>
  ),
  fit: (
    <>
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </>
  ),
  cloud: (
    <>
      <circle cx="6" cy="8" r=".8" /><circle cx="11" cy="6" r=".8" /><circle cx="16" cy="9" r=".8" /><circle cx="8" cy="13" r=".8" /><circle cx="14" cy="14" r=".8" /><circle cx="18" cy="16" r=".8" /><circle cx="5" cy="18" r=".8" /><circle cx="11" cy="18" r=".8" />
    </>
  ),
  reset: (
    <>
      <path d="M4 12a8 8 0 1 0 2.3-5.7" /><path d="M4 4v4h4" />
    </>
  ),
  chevron: (
    <>
      <path d="M7 10l5 5 5-5" />
    </>
  ),
  warning: (
    <>
      <path d="M12 4l9 16H3z" /><path d="M12 10v4M12 17v.5" />
    </>
  ),
  /** Revit's flip control: a door's facing (the side it swings to). */
  flipFacing: (
    <>
      <path d="M4 12h16" strokeDasharray="2 2" /><path d="M12 3v6M9 6l3-3 3 3M12 21v-6M9 18l3 3 3-3" />
    </>
  ),
  /** … and its hand (which jamb it hangs on). */
  flipHand: (
    <>
      <path d="M12 4v16" strokeDasharray="2 2" /><path d="M3 12h6M6 9l-3 3 3 3M21 12h-6M18 9l3 3-3 3" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type CadIconName = keyof typeof PATHS;

/** The icon of a tool or a command (the selection bar's). */
export function commandIcon(command: GeometriCommand | `tool:${GeometriTool}`): CadIconName {
  const name = command.startsWith('tool:') ? command.slice(5) : command;
  return (name in PATHS ? name : 'select') as CadIconName;
}

export function CadIcon({ name, className }: { name: CadIconName; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
